import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { callApi, HttpMethod } from "src/services/apiService";
import { carrierEndpoint } from "../utils/trustCallApi";
import type { TrustCallAdvancedInsight } from "../types";
import { field } from "../utils/insightField";
import { parseTsToDate, safeParse, formatLocal, formatLocalDateOnly } from "../utils/dateHelpers";

interface AdvancedInsightsTabProps {
  phoneNumber: string;
  data: TrustCallAdvancedInsight;
}

// === Lookup tables matching MVC ===
const REPUTATION_CATEGORIES: Record<number, string> = {
  1: "Spammer", 2: "Scammer", 3: "Telemarketer", 4: "Spoofer", 5: "Robocaller",
  6: "Non-compliant", 7: "Public Service", 8: "Healthcare", 9: "Debt Collector",
  10: "Charity", 11: "Political", 12: "Survey", 13: "Reminder Notification", 14: "Callbacks-Conf-Msgs",
};

const REASON_LOOKUP: Record<string, string> = {
  "11": "Honeypot", "12": "Private Exchange", "13": "Wangiri Scam",
  "1": "Algorithm", "2": "Not Allocated", "3": "Invalid",
  "4": "Neighborhood Spoofing", "5": "8XX Not Allocated", "6": "Matching Cg/Cd TN",
  "7": "Malformed", "8": "STIR Verstat Affected", "9": "Carpet Bombing", "10": "Do Not Originate",
};

// === Helpers ===
function formatInt(v: unknown): string {
  if (v == null) return "\u2014";
  const n = Number(v);
  return isNaN(n) ? String(v) : n.toLocaleString();
}

function trunc(s: string, n = 140): string {
  return s.length > n ? s.slice(0, n - 1) + "\u2026" : s;
}

type SubTab = "tns" | "hiya" | "not-spam";

// === Main Component ===
const AdvancedInsightsTab: React.FC<AdvancedInsightsTabProps> = ({ phoneNumber, data: row }) => {
  const [subTab, setSubTab] = useState<SubTab>("tns");
  const [expandedChecks, setExpandedChecks] = useState<Set<number>>(new Set());

  // Hiya reputation checks — for the "Reputation Checks History" table
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const hiyaChecksQuery = useQuery<any>({
    queryKey: ["trustcall", "hiya", phoneNumber],
    queryFn: () => callApi(carrierEndpoint(phoneNumber, "hiya"), HttpMethod.GET),
    enabled: subTab === "hiya" && !!phoneNumber,
  });

  // Hiya details — for Reports + Report Summary
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const hiyaDetailsQuery = useQuery<any[]>({
    queryKey: ["trustcall", "hiya-details", phoneNumber],
    queryFn: () => callApi(carrierEndpoint(phoneNumber, "hiya-details"), HttpMethod.GET),
    enabled: subTab === "hiya" && !!phoneNumber,
  });

  // Not-Spam Reports
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const notSpamQuery = useQuery<any[]>({
    queryKey: ["trustcall", "not-spam-reports", phoneNumber],
    queryFn: () => callApi(carrierEndpoint(phoneNumber, "not-spam-reports"), HttpMethod.GET),
    enabled: subTab === "not-spam" && !!phoneNumber,
  });

  // Track expanded request/response JSON sections per report
  const [expandedReq, setExpandedReq] = useState<Set<number>>(new Set());
  const [expandedRes, setExpandedRes] = useState<Set<number>>(new Set());
  const toggleReq = (idx: number) => setExpandedReq((prev) => { const n = new Set(prev); if (n.has(idx)) n.delete(idx); else n.add(idx); return n; });
  const toggleRes = (idx: number) => setExpandedRes((prev) => { const n = new Set(prev); if (n.has(idx)) n.delete(idx); else n.add(idx); return n; });

  // Parse data once
  // Use field() helper to handle PascalCase vs camelCase API responses
  const f = (name: string) => field(row as Record<string, unknown>, name);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const reputationHistory = (safeParse(f("ReputationHistoryJson")) as any[]) || [];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const callVolumeRaw = (safeParse(f("CallVolJson")) as any[]) || [];
  const callVolumeData = callVolumeRaw
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((p: any) => ({
      _dt: parseTsToDate(p.date || p.ts || p.timestamp),
      vol: (p.callVol || p.count || p.volume || 0) as number,
    }))
    .filter((p): p is { _dt: Date; vol: number } => p._dt != null)
    .sort((a, b) => b._dt.getTime() - a._dt.getTime());

  // TNRM status
  const addedTNRMOn = f("AddedTNRMOn") ? parseTsToDate(f("AddedTNRMOn")) : null;
  const removedTNRMOn = f("RemovedTNRMOn") ? parseTsToDate(f("RemovedTNRMOn")) : null;
  const addedTNRMByName = f("AddedTNRMByName") || null;
  const removedTNRMByName = f("RemovedTNRMByName") || null;

  // TNRM Submission History
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const submissions = (safeParse(f("TnrmSubmissionHistoryJson")) as any[]) || [];

  // TNS Score
  const tnsScore = f("TNRMScore") as number | null | undefined;
  const tnsLastRetrieved = f("TNRMScoreRetrievedOn") ? parseTsToDate(f("TNRMScoreRetrievedOn")) : null;
  const scoreVal = tnsScore != null ? (tnsScore > 0 ? "+" : "") + tnsScore : "\u2014";
  const scoreClass = tnsScore == null ? "" : tnsScore >= 0 ? "text-success" : "text-danger";

  // Override Audits
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const overridesRaw = (safeParse(f("OverrideAuditJSON")) as any[]) || [];
  const actionLabel = (a: string) => (a === "I" ? "Added" : a === "D" ? "Deleted" : (a || "\u2014"));
  const overrides = overridesRaw
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((e: any) => {
      const dt = parseTsToDate(e.eventTS || e.eventTime || e.updatedDate || e.createdDate);
      return {
        dt,
        action: actionLabel(e.action),
        category: REPUTATION_CATEGORIES[Number(e.categoryId)] || (e.categoryId != null ? "Cat " + e.categoryId : "\u2014"),
        scoreId: (e.scoreId || "\u2014") as string,
        by: (e.updatedBy || e.createdBy || "\u2014") as string,
        note: (e.note || "") as string,
      };
    })
    .filter((r) => r.dt != null)
    .sort((a, b) => b.dt!.getTime() - a.dt!.getTime());

  // Crowd Source Events — parse the JSON string into an array.
  // The crowdSourceJSON from the DB contains malformed JSON:
  //   - "ets" dates have embedded quotes: "2026-05-"13T22":"58":50.518"
  //   - "serializedMessage" has unescaped quotes: java.lang."IllegalArgumentException"
  // MVC parseCrowdSource (phone-detail-dialog.js lines 459-470) fixes these with regexes.
  const crowdSourceVal = f("CrowdSourceJSON");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let crowdRaw: any[] = [];
  if (Array.isArray(crowdSourceVal)) {
    crowdRaw = crowdSourceVal;
  } else if (typeof crowdSourceVal === "string" && crowdSourceVal.trim()) {
    let s = crowdSourceVal.trim();
    // Attempt 1: parse as-is
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) { crowdRaw = parsed; s = ""; }
    } catch { /* continue to fixups */ }
    // Attempt 2: apply MVC regex fixups for malformed ets + serializedMessage fields
    if (s) {
      try {
        // Fix ets dates: "ets":"2026-05-"13T22":"58":50.518" → "ets":"2026-05-13T22:58:50.518"
        s = s.replace(/"ets"\s*:\s*"(\d{4})-(\d{2})-"(\d{2})T(\d{2})":"(\d{2})":(\d{2}(?:\.\d+)?)"/g,
          '"ets":"$1-$2-$3T$4:$5:$6"');
        // Fix serializedMessage: escape inner quotes in java.lang."X": patterns
        s = s.replace(/("serializedMessage"\s*:\s*")([\s\S]*?)("(?=\s*[,}]))/g,
          (_m: string, start: string, body: string) => {
            const fixedBody = body
              .replace(/([\w$.]+\.)(?:"([^"]+)")(?=\s*:)/g,
                (_m2: string, pkg: string, word: string) => pkg + '\\"' + word + '\\"')
              .replace(/\r?\n/g, "\\n");
            return start + fixedBody + '"';
          });
        const parsed2 = JSON.parse(s);
        if (Array.isArray(parsed2)) crowdRaw = parsed2;
      } catch { /* give up */ }
    }
  }

  const toNiceCat = (raw: string) => {
    const v = String(raw || "").toLowerCase();
    if (v === "spam") return "Spam";
    if (v === "not-spam") return "Not spam";
    if (v === "scam-fraud") return "Scam/Fraud";
    return raw || "\u2014";
  };

  const crowd = crowdRaw
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((e: any) => {
      const ms = e.ts != null ? e.ts : e.eventTs;
      const cleanIso = (s: unknown) => (typeof s === "string") ? s.replace(/"/g, "") : s;
      const tsArg = (ms != null && !isNaN(Number(ms))) ? Number(ms) : (cleanIso(e.ets) || e.cts);
      const dt = parseTsToDate(tsArg);
      return {
        dt,
        category: toNiceCat(e.csucat),
        source: (e.source || "\u2014") as string,
        comment: (e.cmnt || e.comment || e.serializedMessage || "") as string,
        reportedBy: (e.reportedBy || "\u2014") as string,
        eventId: (e.eventId || "\u2014") as string,
      };
    })
    .filter((r) => r.dt != null)
    .sort((a, b) => b.dt!.getTime() - a.dt!.getTime());

  const renderTnsTab = () => (
    <div>
      {/* TNRM Status */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="tc-card-header px-3 py-2"><h5 className="mb-0 text-white">TNRM Status</h5></div>
            <div className="card-body">
              {addedTNRMOn && (
                <p className="mb-1">
                  <strong>Added to TNRM:</strong> {formatLocal(addedTNRMOn)}
                  {addedTNRMByName && ` by ${addedTNRMByName}`}
                </p>
              )}
              {removedTNRMOn && (
                <p className="mb-1">
                  <strong>Removed from TNRM:</strong> {formatLocal(removedTNRMOn)}
                  {removedTNRMByName && ` by ${removedTNRMByName}`}
                </p>
              )}
              {!addedTNRMOn && !removedTNRMOn && (
                <p className="text-muted mb-0">Not added to TNRM.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* TNRM Submission History */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="tc-card-header px-3 py-2"><h5 className="mb-0 text-white">TNRM Submission History</h5></div>
            <div className="card-body">
              <div className="table-responsive" style={{ maxHeight: 400, overflowY: "auto" }}>
                <table className="table table-sm table-striped align-middle">
                  <thead>
                    <tr>
                      <th>Account</th><th>Submission Type</th><th>Requested On</th>
                      <th>Requested By</th><th>Sent to TNS On</th><th>Pending</th><th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!Array.isArray(submissions) || submissions.length === 0 ? (
                      <tr><td colSpan={7} className="text-muted">No submission history found.</td></tr>
                    ) : (
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      submissions.map((s: any, idx: number) => {
                        const reqOn = s.requestedOn ? parseTsToDate(s.requestedOn) : null;
                        const sentOn = s.sentToTNSOn ? parseTsToDate(s.sentToTNSOn) : null;
                        return (
                          <tr key={idx}>
                            <td>{s.acctId || "\u2014"}</td>
                            <td>{s.submissionType || "\u2014"}</td>
                            <td>{formatLocal(reqOn)}</td>
                            <td>{s.requestedByName || "\u2014"}</td>
                            <td>{formatLocal(sentOn)}</td>
                            <td>
                              {s.needToSendToTNS
                                ? <span className="badge bg-warning text-dark">Yes</span>
                                : <span className="badge bg-success">No</span>}
                            </td>
                            <td>{s.notes || ""}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* TNS Score Cards */}
      <div className="row mb-4">
        <div className="col-md-6">
          <div className="card h-100"><div className="card-body">
            <h5 className="card-title">TNS Score</h5>
            <h2 className={scoreClass}>{scoreVal}</h2>
            <small className="text-muted">Range: -4 to +4</small>
          </div></div>
        </div>
        <div className="col-md-6">
          <div className="card h-100"><div className="card-body">
            <h5 className="card-title">TNS Score Last Retrieved</h5>
            <p className="mb-0">{formatLocal(tnsLastRetrieved)}</p>
          </div></div>
        </div>
      </div>

      {/* Override Audits */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="tc-card-header px-3 py-2"><h5 className="mb-0 text-white">TNS Override Audits</h5></div>
            <div className="card-body">
              <div className="table-responsive" style={{ maxHeight: 400, overflowY: "auto" }}>
                <table className="table table-sm table-striped align-middle">
                  <thead>
                    <tr><th>Date/Time</th><th>Action</th><th>Category</th><th>Score ID</th><th>Updated By</th><th>Note</th></tr>
                  </thead>
                  <tbody>
                    {overrides.length === 0 ? (
                      <tr><td colSpan={6} className="text-muted">No override audits found.</td></tr>
                    ) : (
                      overrides.map((r, idx) => (
                        <tr key={idx}>
                          <td>{formatLocal(r.dt)}</td>
                          <td>{r.action}</td>
                          <td>{r.category}</td>
                          <td>{r.scoreId}</td>
                          <td>{r.by}</td>
                          <td>{r.note}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reputation History */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="tc-card-header px-3 py-2"><h5 className="mb-0 text-white">TNS Reputation History</h5></div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-sm table-striped">
                  <thead><tr><th>Date/Time</th><th>Category</th><th>Score</th><th>Reason ID</th></tr></thead>
                  <tbody>
                    {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                    {(Array.isArray(reputationHistory) ? reputationHistory : []).map((item: any, idx: number) => {
                      const tsRaw = item.eventTS || item.eventTs || item.ts || item.timestamp || null;
                      const dt = parseTsToDate(tsRaw);
                      const cat = item.tagHistory ? item.tagHistory.category : null;
                      const score = item.tagHistory ? item.tagHistory.score : null;
                      const categoryName = REPUTATION_CATEGORIES[Number(cat)] || ("Unknown (Cat " + (cat || "\u2014") + ")");
                      const sc = score != null ? (score >= 0 ? "text-success" : "text-danger") : "";
                      const reasonId = item.reasonId != null ? String(item.reasonId) : null;
                      const reasonText = reasonId && REASON_LOOKUP[reasonId] ? REASON_LOOKUP[reasonId] : (reasonId ? "Reason " + reasonId : "\u2014");
                      return (
                        <tr key={idx}>
                          <td>{formatLocal(dt)}</td>
                          <td>{categoryName}</td>
                          <td className={sc}>{score == null ? "\u2014" : (score > 0 ? "+" : "") + score}</td>
                          <td>{reasonText}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Call Volume */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="tc-card-header px-3 py-2"><h5 className="mb-0 text-white">TNS Call Volume</h5></div>
            <div className="card-body">
              <div className="table-responsive" style={{ maxHeight: 400, overflowY: "auto" }}>
                <table className="table table-sm table-striped">
                  <thead><tr><th>Date</th><th>Call Volume</th></tr></thead>
                  <tbody>
                    {callVolumeData.map((item, idx) => (
                      <tr key={idx}>
                        <td>{formatLocalDateOnly(item._dt)}</td>
                        <td>{formatInt(item.vol)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Crowd Source Events */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="tc-card-header px-3 py-2"><h5 className="mb-0 text-white">TNS Crowd Source Events</h5></div>
            <div className="card-body">
              <div className="table-responsive" style={{ maxHeight: 400, overflowY: "auto" }}>
                <table className="table table-sm table-striped align-middle">
                  <thead>
                    <tr><th>Date/Time</th><th>CS Category</th><th>Source</th><th>Comment</th><th>Reported By</th><th>Event ID</th></tr>
                  </thead>
                  <tbody>
                    {crowd.length === 0 ? (
                      <tr><td colSpan={6} className="text-muted">No crowd source events found.</td></tr>
                    ) : (
                      crowd.map((r, idx) => {
                        const catClass = r.category === "Spam" || r.category === "Scam/Fraud"
                          ? "text-danger"
                          : r.category === "Not spam" ? "text-success" : "";
                        return (
                          <tr key={idx}>
                            <td>{formatLocal(r.dt)}</td>
                            <td className={catClass}>{r.category}</td>
                            <td>{r.source}</td>
                            <td title={r.comment}>{trunc(r.comment)}</td>
                            <td>{r.reportedBy}</td>
                            <td>{r.eventId}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // === Hiya Category Lookup — matches MVC HIYA_CATEGORY_LOOKUP ===
  const HIYA_CATS: Record<number, string> = {
    1: "Not Spam", 2: "General Spam", 3: "Account Services", 4: "Political Call",
    5: "Nonprofit", 6: "Telemarketer", 7: "Survey", 8: "Fraud", 10: "Robocaller",
  };
  const getCatName = (id: number | null) => (id != null ? HIYA_CATS[id] || `Unknown (${id})` : "\u2014");

  const toggleCheck = (idx: number) => {
    setExpandedChecks((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const renderHiyaTab = () => {
    const isLoading = hiyaChecksQuery.isLoading || hiyaDetailsQuery.isLoading;
    if (isLoading) return (
      <div className="text-center py-4">
        <div className="spinner-border spinner-border-sm" role="status" />
        <span className="ms-2">Loading Hiya details...</span>
      </div>
    );

    // --- Reputation Checks History (from /hiya endpoint) ---
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const checks: any[] = hiyaChecksQuery.data?.checks || [];

    // --- Reports + Summary (from /hiya-details endpoint) ---
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const detailRows: any[] = Array.isArray(hiyaDetailsQuery.data) ? hiyaDetailsQuery.data : [];
    const detailRow = detailRows.length > 0 ? detailRows[0] : null;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const parsedReports: any = detailRow ? (safeParse(detailRow.ReportsJson || detailRow.reportsJson || detailRow.reportsJSON) || {}) : {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const stats: any = detailRow ? (safeParse(detailRow.ReportsStats || detailRow.reportsStats) || {}) : {};

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const reports: any[] = (Array.isArray(parsedReports.reports) ? parsedReports.reports : [])
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .map((r: any) => {
        const catId = r?.category != null ? Number(r.category) : null;
        const comment = r?.comment && typeof r.comment === "object" ? (r.comment.str || null) : (r?.comment || null);
        return { tsIso: r?.timestamp || null, categoryId: catId, categoryName: getCatName(catId), comment };
      })
      .sort((a: { tsIso: string | null }, b: { tsIso: string | null }) =>
        (b.tsIso ? Date.parse(b.tsIso) : 0) - (a.tsIso ? Date.parse(a.tsIso) : 0));

    const total = stats.reportCounts?.total ?? reports.length;
    const spam = stats.reportCounts?.spam ?? null;
    const notSpam = stats.reportCounts?.notSpam ?? null;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let categoryCounts: { category: number; count: number }[] = Array.isArray(stats.categoryCounts) ? stats.categoryCounts : [];
    if (categoryCounts.length === 0) {
      const fallback: Record<number, number> = {};
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      reports.forEach((r: any) => { if (r.categoryId != null) fallback[r.categoryId] = (fallback[r.categoryId] || 0) + 1; });
      categoryCounts = Object.keys(fallback).map((k) => ({ category: Number(k), count: fallback[Number(k)] }));
    }
    categoryCounts.sort((a, b) => b.count - a.count);

    return (
      <div>
        {/* Reputation Checks History */}
        <div className="card mb-3">
          <div className="tc-card-header px-3 py-2"><h6 className="mb-0 text-white">Reputation Checks History</h6></div>
          <div className="card-body">
            {checks.length === 0 ? (
              <div className="text-muted">No Hiya reputation checks found for this phone number.</div>
            ) : (
              <div className="table-responsive" style={{ maxHeight: 400, overflowY: "auto" }}>
                <table className="table table-sm table-striped align-middle">
                  <thead><tr><th>Date/Time</th><th>Operation</th><th>Status</th><th>Duration</th><th></th></tr></thead>
                  <tbody>
                    {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                    {checks.map((c: any, idx: number) => {
                      const dt = c.requestedAt ? formatLocal(parseTsToDate(c.requestedAt)) : "\u2014";
                      const statusCode = c.responseStatusCode;
                      const statusClass = statusCode >= 200 && statusCode < 300 ? "text-success" : "text-danger";
                      const isExpanded = expandedChecks.has(idx);
                      let prettyResponse = "\u2014";
                      try { prettyResponse = JSON.stringify(JSON.parse(c.responseBody), null, 2); } catch { prettyResponse = c.responseBody || "\u2014"; }
                      return (
                        <React.Fragment key={idx}>
                          <tr>
                            <td>{dt}</td>
                            <td>{c.operationType || "\u2014"}</td>
                            <td className={statusClass}>{statusCode}</td>
                            <td>{c.durationMs != null ? c.durationMs + "ms" : "\u2014"}</td>
                            <td>
                              <a href="#" onClick={(e) => { e.preventDefault(); toggleCheck(idx); }}>
                                Details
                              </a>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={5} className="p-0">
                                <pre className="tc-json-block p-2 m-0 small" style={{ maxHeight: 300, overflow: "auto", whiteSpace: "pre-wrap" }}>
                                  {prettyResponse}
                                </pre>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Reports */}
        <div className="card mb-3">
          <div className="tc-card-header px-3 py-2"><h6 className="mb-0 text-white">Reports</h6></div>
          <div className="card-body">
            {reports.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-sm table-striped align-middle">
                  <thead><tr><th>Date &amp; Time</th><th>Category</th><th>Comment</th></tr></thead>
                  <tbody>
                    {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                    {reports.map((r: any, idx: number) => (
                      <tr key={idx}>
                        <td>{formatLocal(parseTsToDate(r.tsIso))}</td>
                        <td>{r.categoryName}</td>
                        <td>{r.comment || "\u2014"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-muted">No reports.</div>
            )}
          </div>
        </div>

        {/* Report Summary */}
        <div className="card">
          <div className="tc-card-header px-3 py-2"><h6 className="mb-0 text-white">Report Summary</h6></div>
          <div className="card-body">
            <div className="mb-3">
              <div><strong>Total Reports:</strong> {formatInt(total)}</div>
              <div><strong>Spam Reports:</strong> {formatInt(spam)}</div>
              <div><strong>Not Spam:</strong> {formatInt(notSpam)}</div>
            </div>
            <div className="table-responsive">
              <table className="table table-sm table-striped align-middle">
                <thead><tr><th>Category</th><th className="text-end">Count</th></tr></thead>
                <tbody>
                  {categoryCounts.map((item, idx) => (
                    <tr key={idx}>
                      <td>{getCatName(item.category)}</td>
                      <td className="text-end">{formatInt(item.count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderNotSpamTab = () => {
    if (notSpamQuery.isLoading) return (
      <div className="text-center py-4">
        <div className="spinner-border spinner-border-sm" role="status" />
        <span className="ms-2">Loading Not-Spam reports...</span>
      </div>
    );
    if (notSpamQuery.isError) return <div className="alert alert-danger mb-0">Failed to load Not-Spam reports.</div>;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const reports: any[] = Array.isArray(notSpamQuery.data) ? notSpamQuery.data : [];
    if (reports.length === 0) {
      return <div className="alert alert-info mb-0">No Not-Spam reports found for this phone number.</div>;
    }

    // Field accessor helper for casing flexibility (PascalCase vs camelCase)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rf = (obj: any, name: string) => field(obj as Record<string, unknown>, name);

    return (
      <div className="card">
        <div className="tc-card-header px-3 py-2"><h6 className="mb-0 text-white">Not-Spam Reports Filed</h6></div>
        <div className="card-body">
          <p className="text-muted mb-3">Total reports: <strong>{reports.length}</strong></p>

          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {reports.map((report: any, idx: number) => {
            const reportDate = rf(report, "ReportedOn") ? formatLocal(parseTsToDate(rf(report, "ReportedOn"))) : "\u2014";
            const carrier = rf(report, "Carrier") || "\u2014";
            const reportId = rf(report, "ReportId") || idx;

            const reqJson = rf(report, "ReportJSONSent") as string | null;
            const resJson = rf(report, "ResponseJSONReceived") as string | null;
            let formattedReq = reqJson || "";
            try { if (reqJson) formattedReq = JSON.stringify(JSON.parse(reqJson), null, 2); } catch { /* keep raw */ }
            let formattedRes = resJson || "";
            try { if (resJson) formattedRes = JSON.stringify(JSON.parse(resJson), null, 2); } catch { /* keep raw */ }

            return (
              <div className="card mb-2" key={idx}>
                <div className="card-header tc-json-block">
                  <div className="row">
                    <div className="col-md-6"><strong>Report #{reportId}</strong></div>
                    <div className="col-md-6 text-end"><small className="text-muted">{reportDate}</small></div>
                  </div>
                </div>
                <div className="card-body">
                  <div className="row mb-1">
                    <div className="col-md-2"><strong>Carrier:</strong></div>
                    <div className="col-md-10">{carrier}</div>
                  </div>

                  {reqJson && (
                    <div className="row mb-2">
                      <div className="col-md-12">
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          type="button"
                          onClick={() => toggleReq(idx)}
                        >
                          View Request JSON
                        </button>
                        {expandedReq.has(idx) && (
                          <pre className="tc-json-block p-2 mt-2" style={{ maxHeight: 300, overflowY: "auto" }}>
                            {formattedReq}
                          </pre>
                        )}
                      </div>
                    </div>
                  )}

                  {resJson && (
                    <div className="row">
                      <div className="col-md-12">
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          type="button"
                          onClick={() => toggleRes(idx)}
                        >
                          View Response JSON
                        </button>
                        {expandedRes.has(idx) && (
                          <pre className="tc-json-block p-2 mt-2" style={{ maxHeight: 300, overflowY: "auto" }}>
                            {formattedRes}
                          </pre>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const subTabs: { key: SubTab; label: string }[] = [
    { key: "tns", label: "TNS" },
    { key: "hiya", label: "Hiya" },
    { key: "not-spam", label: "Not-Spam Reports" },
  ];

  return (
    <div style={{ paddingTop: 10 }}>
      {/* Sub-tabs */}
      <div style={{ display: "flex", gap: 12, marginBottom: 10, borderBottom: "2px solid #dee2e6", paddingBottom: 4 }}>
        {subTabs.map((t) => {
          const isActive = subTab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setSubTab(t.key)}
              type="button"
              style={{
                background: "none",
                border: "none",
                borderBottom: isActive ? "2px solid #1b6ec2" : "2px solid transparent",
                padding: "4px 2px",
                marginBottom: "-5px",
                color: isActive ? "#1b6ec2" : "#495057",
                fontWeight: isActive ? 600 : 400,
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {subTab === "tns" && renderTnsTab()}
      {subTab === "hiya" && renderHiyaTab()}
      {subTab === "not-spam" && renderNotSpamTab()}
    </div>
  );
};

export default AdvancedInsightsTab;
